import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { LeadStatus, SiteStatus } from '@shared/types';

// Lead statuses that mean the business has been reached out to.
const CONTACTED_LEAD_STATUSES: ReadonlySet<LeadStatus> = new Set(['contacted', 'interested', 'converted']);
// Site statuses that mean the site was sent to (or bought by) the business.
const OUTREACH_SITE_STATUSES: ReadonlySet<SiteStatus> = new Set(['sent', 'sold']);

/**
 * Record that a site was sent to its business. The lead moves to "contacted"
 * too, unless it has already progressed further, so every screen that reads
 * lead status agrees with the outreach that actually happened.
 */
export async function markSiteSent(siteId: string, leadId?: string | null): Promise<void> {
  await updateDoc(doc(db, 'sites', siteId), { status: 'sent', sentAt: serverTimestamp() });
  if (!leadId) return;
  const leadRef = doc(db, 'leads', leadId);
  const lead = await getDoc(leadRef);
  if (lead.exists() && lead.data().status === 'new') {
    await updateDoc(leadRef, { status: 'contacted', contactedAt: serverTimestamp() });
  }
}

/**
 * Distinct businesses contacted: any lead whose site was sent or sold, plus
 * any lead marked contacted or beyond. Counting both covers outreach recorded
 * before leads were updated alongside sites.
 */
export function countContactedBusinesses(
  leads: Array<{ id: string; status?: LeadStatus }> | undefined,
  sites: Array<{ id: string; leadId?: string; status?: SiteStatus }> | undefined,
): number {
  const ids = new Set<string>();
  for (const site of sites ?? []) {
    if (site.status && OUTREACH_SITE_STATUSES.has(site.status)) ids.add(site.leadId || `site:${site.id}`);
  }
  for (const lead of leads ?? []) {
    if (lead.status && CONTACTED_LEAD_STATUSES.has(lead.status)) ids.add(lead.id);
  }
  return ids.size;
}
